import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEqual as _isEqual, isEmpty as _isEmpty } from 'lodash';

import { PillLabel, Media, Swiper } from 'components';
import { FarmVisualizerBanner } from 'components/farm-product-detail/components';
import {
  ProductHeroSliderStyled,
  HeroSliderInner,
  ProductSlideStyled,
} from 'components/farm-product-detail/components/farm-carousel-mobile/FarmCarouselMobile.styled';

import { FARMSTAND_SKUS } from 'constants/sku';
import growingEnvironments from 'constants/growingEnvironments';
import { trackClick } from 'utils/googleTagManager';

const { OUTDOOR, INDOOR } = growingEnvironments;

const sliderBaseConfig = {
  navigation: false,
  pagination: true,
  speed: 1000,
  slidesPerView: 1,
  spaceBetween: 12,
};

const FarmCarouselMobile = ({ size, data = [], dataGatsby = [], promo, playVideoComponent, visualizerData, environment }) => {
  const [swiper, setSwiper] = useState(null);
  const finalData = useMemo(() => {
    const imgs = dataGatsby?.length > 0 ? dataGatsby : data;
    return imgs.filter(({ title }) => !environment || title.includes(environment) || (!title.includes(INDOOR) && !title.includes(OUTDOOR)));
  }, [data, dataGatsby, environment]);
  const [hasImageSku, setHasImageSku] = useState(!!finalData?.[0]?.description);

  useEffect(() => {
    if (size && finalData?.length) {
      const farmImgIndex = finalData.findIndex((e) => e.description.includes(size));
      if (farmImgIndex > -1 && farmImgIndex !== swiper?.activeIndex) {
        swiper?.slideTo?.(farmImgIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, swiper]);

  const renderSlide = useCallback((slideItem, index) => {
    return (
      <ProductSlideStyled key={index}>
        <Media altDescription={FARMSTAND_SKUS.includes(slideItem?.description) && slideItem.title} asset={slideItem} nativeWidth={550} />
      </ProductSlideStyled>
    );
  }, []);

  const onSlideIndexChange = ({ activeIndex, previousIndex }) => {
    setHasImageSku(!!finalData?.[activeIndex]?.description);
    if (previousIndex) {
      // only track index change by user, don't track initial slide change that happens programmatically
      trackClick({ action: 'FS Image' });
    }
  };

  const extraProps = {
    onSlideChange: onSlideIndexChange,
    onSwiper: setSwiper,
  };

  return (
    <ProductHeroSliderStyled>
      {!!promo && hasImageSku && <PillLabel content={promo} />}
      <HeroSliderInner>
        <Swiper swiperProps={{ ...sliderBaseConfig, ...extraProps }} renderSlideCard={renderSlide} items={finalData} />
      </HeroSliderInner>
      {!!visualizerData && !_isEmpty(visualizerData) && (
        <FarmVisualizerBanner title={visualizerData.title} body={visualizerData.body} mobileCtas={visualizerData.additionalButtons} />
      )}
      {playVideoComponent && playVideoComponent()}
    </ProductHeroSliderStyled>
  );
};

FarmCarouselMobile.displayName = 'FarmCarouselMobile';

FarmCarouselMobile.propTypes = {
  size: PropTypes.any,
  data: PropTypes.array,
  dataGatsby: PropTypes.array,
  promo: PropTypes.string,
  playVideoComponent: PropTypes.any,
  visualizerData: PropTypes.object,
  environment: PropTypes.string,
};

const shouldPreventUpdate = (prevProps, nextProps) => {
  return (
    _isEqual(prevProps.data, nextProps.data) &&
    _isEqual(prevProps.dataGatsby, nextProps.dataGatsby) &&
    _isEqual(prevProps.visualizerData, nextProps.visualizerData) &&
    prevProps.size === nextProps.size &&
    prevProps.promo === nextProps.promo &&
    prevProps.environment === nextProps.environment &&
    !!prevProps.playVideoComponent === !!nextProps.playVideoComponent
  );
};

export default memo(FarmCarouselMobile, shouldPreventUpdate);
